import React, { useMemo } from 'react';
import { CSS } from '@dnd-kit/utilities';
import type { Transform } from '@dnd-kit/utilities';
import classNames from 'classnames';
import HeaderItem, { HeaderItemProps } from 'src/components/ui/HeaderItem/HeaderItem';
import CardContainer from '../../layouts/CardContainer/CardContainer';
import styles from './StatisticRow.module.scss';

export interface StatisticRowProps {
  items: HeaderItemProps[];
  setNodeRef?: (node: HTMLElement | null) => void;
  transform?: Transform | null;
  transition?: string;
  isStatistic?: boolean;
  loading?: boolean;
}

const Skeleton = () => (
  <div className="flex flex-col p-20 min-h-[160px] w-full justify-between border-subtle-right">
    <div className="animate-pulse h-[22px] w-full max-w-[112px] surface-skeleton-faded rounded-md" />
    <div className="flex flex-col gap-[8px] max-w-[239px]">
      <div className="animate-pulse h-[22px] w-full surface-skeleton-faded rounded-md" />
      <div className="animate-pulse h-[22px] w-full surface-skeleton-faded rounded-md" />
    </div>
  </div>
);

const StatisticRow: React.FC<StatisticRowProps> = ({
  items,
  setNodeRef,
  transform,
  transition,
  isStatistic,
  loading,
}) => {
  const style = useMemo(
    () => ({
      transform: CSS.Transform.toString(transform || null),
      transition,
    }),
    [transform, transition],
  );

  const kpiItems = useMemo(() => items.filter(item => item.kpiNumber), [items]);

  return (
    <div className="relative">
      <CardContainer className={classNames(styles.root)} ref={setNodeRef} style={style} isRow disablePadding>
        {loading ? (
          <div className="w-full grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(item => (
              <Skeleton key={item} />
            ))}
          </div>
        ) : (
          <>
            {kpiItems.map(item => (
              <HeaderItem
                key={item.title}
                percentage={item.percentage}
                title={item.title}
                kpiNumber={item.kpiNumber}
                onSelect={item.onSelect}
                caption={item.caption}
                information={item.information}
                isStatistic={isStatistic}
                keyValues={item.keyValues}
                progressColor={item.progressColor}
                description={item.description}
              />
            ))}
          </>
        )}
      </CardContainer>
      <div className={classNames(styles.rightShadow)} />
    </div>
  );
};

export default StatisticRow;
